import { useEffect, useState } from "react";
import { X, Download, Share, MoreVertical } from "lucide-react";

const DISMISS_KEY = "clc.install-prompt-dismissed-at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  // iOS Safari uses `navigator.standalone`; modern engines use the media query.
  // Either path is the canonical "running as installed PWA" signal.
  return (
    window.navigator.standalone === true
    || window.matchMedia("(display-mode: standalone)").matches
    || window.matchMedia("(display-mode: window-controls-overlay)").matches
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  // iPad on iOS 13+ reports as Mac in UA but has touch support — both ifs covered.
  return /iPhone|iPad|iPod/.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // CriOS = Chrome on iOS (also can't install). EdgiOS = Edge on iOS. FxiOS = Firefox.
  return !/(CriOS|EdgiOS|FxiOS|OPiOS|YaBrowser)/.test(ua);
}

function isRecentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts > 0 && Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

function dismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (err) {
    // Safari private mode / quota exhausted — the user just sees the
    // prompt again next visit, which is acceptable degradation.
    console.warn("[InstallPrompt] could not persist dismissal:", err);
  }
}

/**
 * Install prompt — two flavours:
 *  · iOS Safari → static coaching card showing the "Share → Add to Home Screen"
 *    gesture (iOS Safari can't be programmatically prompted).
 *  · Anything that fires `beforeinstallprompt` (Chrome on Android, Edge, etc.)
 *    → captures the event and shows a styled bottom banner with one-tap install.
 *
 * Dismissals are remembered for 14 days so we don't pester users.
 * Hidden entirely when running as a standalone PWA.
 */
export function InstallPrompt() {
  const [chromePrompt, setChromePrompt] = useState(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    // Path A — Chromium browsers fire beforeinstallprompt with an installable event.
    const handler = (e) => {
      e.preventDefault(); // we'll trigger it ourselves on user gesture
      setChromePrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Path B — iOS Safari. We have to coach the gesture manually.
    if (isIOSSafari()) {
      // Slight delay so we don't disrupt first-page-load reading.
      const t = setTimeout(() => setShowIos(true), 2500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerChromeInstall = async () => {
    if (!chromePrompt) return;
    try {
      chromePrompt.prompt();
      const choice = await chromePrompt.userChoice;
      if (choice.outcome === "accepted") {
        setChromePrompt(null);
      } else {
        dismiss(); // don't re-show for 14 days
        setChromePrompt(null);
      }
    } catch {
      setChromePrompt(null);
    }
  };

  const close = (which) => {
    dismiss();
    if (which === "ios") setShowIos(false);
    else setChromePrompt(null);
  };

  // Listen for the `appinstalled` event — fired by every modern engine when
  // the user accepts. Stash a flag so we never re-prompt on this device.
  useEffect(() => {
    const onInstalled = () => {
      dismiss();
      setChromePrompt(null);
      setShowIos(false);
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  // -- iOS coaching card ----------------------------------------------------
  if (showIos) {
    return (
      <div
        className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:w-[360px] z-[60] rounded-2xl border border-[#C9A84C]/30 bg-[#1A2744] text-white shadow-2xl p-4"
        data-testid="ios-install-prompt"
        role="dialog"
        aria-label="Install Coffee Lovers Café"
      >
        <button
          onClick={() => close("ios")}
          className="absolute top-3 right-3 text-white/60 hover:text-white"
          aria-label="Dismiss install prompt"
          data-testid="ios-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3 mb-2">
          <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-lg" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Install app</div>
            <div className="text-sm font-semibold">Get the full Coffee Lovers experience</div>
          </div>
        </div>
        <p className="text-xs text-white/75 mb-3 leading-relaxed">
          Add to your Home Screen to clock in/out faster, get push notifications and run full-screen.
        </p>
        <ol className="text-[12px] text-white/85 space-y-2 mb-1">
          <li className="flex items-center gap-2">
            <span className="inline-flex w-5 h-5 items-center justify-center rounded bg-white/10 text-[10px] font-mono shrink-0">1</span>
            Tap the <Share className="inline h-3.5 w-3.5 -mt-0.5 mx-1 text-[#C9A84C]" /> share icon at the bottom of Safari
          </li>
          <li className="flex items-center gap-2">
            <span className="inline-flex w-5 h-5 items-center justify-center rounded bg-white/10 text-[10px] font-mono shrink-0">2</span>
            Choose <strong>"Add to Home Screen"</strong>
          </li>
        </ol>
      </div>
    );
  }

  // -- Chromium install banner ---------------------------------------------
  if (chromePrompt) {
    return (
      <div
        className="fixed bottom-4 inset-x-3 sm:inset-x-auto sm:right-4 sm:w-[380px] z-[60] rounded-2xl border border-[#C9A84C]/30 bg-white shadow-2xl p-4"
        data-testid="chrome-install-prompt"
        role="dialog"
        aria-label="Install Coffee Lovers Café"
      >
        <button
          onClick={() => close("chrome")}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          aria-label="Dismiss install prompt"
          data-testid="chrome-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Install app</div>
            <div className="text-sm font-semibold text-[#1A2744] truncate">Coffee Lovers Café · Staff Portal</div>
            <div className="text-[11px] text-gray-500">Clock in/out faster, get push notifications, run full-screen</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => close("chrome")}
            className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded-lg"
            data-testid="chrome-install-not-now"
          >
            Not now
          </button>
          <button
            onClick={triggerChromeInstall}
            className="flex-1 px-3 py-2 text-xs font-semibold bg-[#1A2744] text-white rounded-lg hover:bg-[#243559] inline-flex items-center justify-center gap-1.5"
            data-testid="chrome-install-confirm"
          >
            <Download className="h-3.5 w-3.5" /> Install
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// Tiny helper exposed to other components that need to render different UI
// when the app is running in installed-PWA mode (e.g. omit the "Install"
// menu item). Hook-free so it can be called outside React if needed.
InstallPrompt.isStandalone = isStandalone;
